/**
 * DateRangeFilter — Sprint 04-30 P2
 *
 * Two date dimensions on the search Dashboard:
 *   - Published date (when the tender went live)
 *   - Expiry / Closing date (when bids close)
 *
 * Each dimension has preset chips relative to "today" plus a custom
 * range. Applies via URL params: from_date / to_date / closing_from /
 * closing_to (the ones the existing Dashboard already reads). Closing on
 * Apply is enough — the Dashboard's effect dependency on these params
 * triggers a fresh fetch.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addDays, subDays, addMonths } from 'date-fns';

const fmt = (d) => format(d, 'yyyy-MM-dd');

// Preset shape: { id, label, range: () => ({from, to}) | null }
const PUB_PRESETS = [
  { id: 'pub-7',   label: 'Last 7 days',  range: () => ({ from: fmt(subDays(new Date(), 7)),  to: fmt(new Date()) }) },
  { id: 'pub-14',  label: 'Last 14 days', range: () => ({ from: fmt(subDays(new Date(), 14)), to: fmt(new Date()) }) },
  { id: 'pub-30',  label: 'Last 30 days', range: () => ({ from: fmt(subDays(new Date(), 30)), to: fmt(new Date()) }) },
  { id: 'pub-mtd', label: 'This month',   range: () => ({ from: fmt(startOfMonth(new Date())), to: fmt(new Date()) }) },
];

const EXP_PRESETS = [
  { id: 'exp-7',   label: 'Next 7 days',     range: () => ({ from: fmt(new Date()), to: fmt(addDays(new Date(), 7)) }) },
  { id: 'exp-10',  label: 'Next 10 days',    range: () => ({ from: fmt(new Date()), to: fmt(addDays(new Date(), 10)) }) },
  { id: 'exp-14',  label: 'Next 2 weeks',    range: () => ({ from: fmt(new Date()), to: fmt(addDays(new Date(), 14)) }) },
  { id: 'exp-30',  label: 'Next 30 days',    range: () => ({ from: fmt(new Date()), to: fmt(addDays(new Date(), 30)) }) },
  { id: 'exp-eom', label: 'End of month',    range: () => ({ from: fmt(new Date()), to: fmt(endOfMonth(new Date())) }) },
  { id: 'exp-eonm',label: 'End of next month', range: () => ({ from: fmt(new Date()), to: fmt(endOfMonth(addMonths(new Date(), 1))) }) },
];

const matchesPreset = (presets, from, to) => {
  if (!from || !to) return null;
  for (const p of presets) {
    const r = p.range();
    if (r.from === from && r.to === to) return p.id;
  }
  return null;
};

const DateRangeFilter = ({
  fromDate, toDate, closingFrom, closingTo, onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [pubFrom, setPubFrom] = useState(fromDate || '');
  const [pubTo, setPubTo] = useState(toDate || '');
  const [expFrom, setExpFrom] = useState(closingFrom || '');
  const [expTo, setExpTo] = useState(closingTo || '');
  const popRef = useRef(null);

  // sync from URL when caller changes
  useEffect(() => { setPubFrom(fromDate || ''); }, [fromDate]);
  useEffect(() => { setPubTo(toDate || ''); }, [toDate]);
  useEffect(() => { setExpFrom(closingFrom || ''); }, [closingFrom]);
  useEffect(() => { setExpTo(closingTo || ''); }, [closingTo]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (fromDate || toDate) n++;
    if (closingFrom || closingTo) n++;
    return n;
  }, [fromDate, toDate, closingFrom, closingTo]);

  const activePub = matchesPreset(PUB_PRESETS, pubFrom, pubTo);
  const activeExp = matchesPreset(EXP_PRESETS, expFrom, expTo);

  const applyPreset = (presets, set, p) => {
    const r = p.range();
    set.from(r.from);
    set.to(r.to);
  };

  const apply = () => {
    onApply({
      from_date: pubFrom || '',
      to_date: pubTo || '',
      closing_from: expFrom || '',
      closing_to: expTo || '',
    });
    setOpen(false);
  };

  const clearAll = () => {
    setPubFrom(''); setPubTo(''); setExpFrom(''); setExpTo('');
    onApply({ from_date: '', to_date: '', closing_from: '', closing_to: '' });
    setOpen(false);
  };

  return (
    <div className="date-filter-wrap" ref={popRef}>
      <button
        type="button"
        className={`date-filter-btn ${activeCount > 0 ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="Filter by publication or closing date"
      >
        <Calendar size={14} />
        Date filters
        {activeCount > 0 && <span className="date-filter-badge">{activeCount}</span>}
      </button>

      {open && (
        <div className="date-filter-popover" role="dialog">
          <div className="date-filter-section">
            <div className="date-filter-section-head">
              <strong>Published date</strong>
              {(pubFrom || pubTo) && (
                <button type="button" className="date-filter-clear" onClick={() => { setPubFrom(''); setPubTo(''); }}>
                  <X size={12} /> clear
                </button>
              )}
            </div>
            <div className="date-filter-presets">
              {PUB_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`date-filter-chip ${activePub === p.id ? 'active' : ''}`}
                  onClick={() => applyPreset(PUB_PRESETS, { from: setPubFrom, to: setPubTo }, p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="date-filter-custom">
              <label>From <input type="date" value={pubFrom} onChange={(e) => setPubFrom(e.target.value)} /></label>
              <label>To   <input type="date" value={pubTo}   onChange={(e) => setPubTo(e.target.value)}   /></label>
            </div>
          </div>

          <div className="date-filter-section">
            <div className="date-filter-section-head">
              <strong>Closing date (expiry)</strong>
              {(expFrom || expTo) && (
                <button type="button" className="date-filter-clear" onClick={() => { setExpFrom(''); setExpTo(''); }}>
                  <X size={12} /> clear
                </button>
              )}
            </div>
            <div className="date-filter-presets">
              {EXP_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`date-filter-chip ${activeExp === p.id ? 'active' : ''}`}
                  onClick={() => applyPreset(EXP_PRESETS, { from: setExpFrom, to: setExpTo }, p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="date-filter-custom">
              <label>From <input type="date" value={expFrom} onChange={(e) => setExpFrom(e.target.value)} /></label>
              <label>To   <input type="date" value={expTo}   onChange={(e) => setExpTo(e.target.value)}   /></label>
            </div>
          </div>

          <div className="date-filter-actions">
            <button type="button" className="date-filter-link" onClick={clearAll}>Clear all</button>
            <button type="button" className="date-filter-apply" onClick={apply}>Apply filters</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
